/**
 * Migrates or reverses an order by token.
 *
 * @param token - The order token value to migrate.
 * @param authToken - Bearer token (required if reverse = false).
 * @param options - Migration options:
 *   - reverse: If true, disassociates the order and sets store address.
 *   - storeNumber: Required if reverse = true.
 * @returns A success message string or null on failure.
 */
export async function migrateOrder(
  orderToken: string,
  authToken: string | null,
  options: {
    reverse?: boolean;
    storeNumber?: string | number;
  } = {}
): Promise<string | null> {
  const { reverse = false, storeNumber } = options;

  // Enforce required params
  if (!reverse && !authToken) {
    console.error('Cannot migrate order: authToken is required when reverse is false');
    return null;
  }

  if (reverse && !storeNumber) {
    console.error('Cannot reverse order: storeNumber is required');
    return null;
  }

  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`/api/orders/${orderToken}/migrate`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        reverse,
        storeNumber: storeNumber !== undefined ? Number(storeNumber) : undefined,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      console.error(`Error migrating order:`, error?.error || response.statusText);
      return null;
    }

    const data = await response.json();
    return data.message || 'Order migration succeeded';
  } catch (err) {
    console.error('Failed to migrate order:', err);
    return null;
  }
}
